import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Set a New Password | Warrior Volunteer" },
      {
        name: "description",
        content: "Choose a new password for your T1D Warriors volunteer account.",
      },
      { property: "og:title", content: "Set a New Password | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Choose a new password for your T1D Warriors volunteer account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState<"checking" | "ok" | "invalid">("checking");
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase delivers the recovery session in the URL hash and exchanges it
    // on load, so wait for the auth client to settle before deciding.
    let active = true;

    // An expired or already-used link comes back as an error in the hash. Treat
    // that as invalid even if an unrelated session is present in this browser.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const hashError = hash.get("error_description") ?? hash.get("error");
    if (hashError) {
      setLinkError(hashError.replace(/\+/g, " "));
      setReady("invalid");
      return;
    }

    const markReady = () => {
      setReady("ok");
      void supabase.auth.getUser().then(({ data }) => {
        if (active) setAccountEmail(data.user?.email ?? null);
      });
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY" || session) markReady();
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) markReady();
      else if (!window.location.hash.includes("access_token")) setReady("invalid");
    });

    const timer = window.setTimeout(() => {
      if (active) setReady((prev) => (prev === "checking" ? "invalid" : prev));
    }, 4000);

    return () => {
      active = false;
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    const confirm = String(form.get("confirm") ?? "");
    if (password !== confirm) {
      toast.error("Those passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You are signed in.");
      navigate({ to: "/dashboard", replace: true });
    } catch (error) {
      reportHandledSync(error, { feature: "reset-password", action: "handleSubmit" });
      toast.error(error instanceof Error ? error.message : "Could not update the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-light flex min-h-screen flex-col items-center justify-center bg-background px-5 py-16">
      <Link to="/" className="mb-8 inline-flex">
        <img src="/t1dw-logo.png" alt="T1D Warriors" className="h-10 w-auto" />
      </Link>

      <div className="panel w-full max-w-md p-8">
        <p className="t-eyebrow">Warrior Volunteer</p>
        <h1 className="t-h2 mt-3 text-ice">Set a new password</h1>

        {ready === "checking" ? (
          <p className="mt-5 text-sm text-muted-foreground">Checking your reset link...</p>
        ) : ready === "invalid" ? (
          <>
            <p className="mt-5 text-sm text-muted-foreground">
              {linkError
                ? `This reset link is no longer valid (${linkError}).`
                : "This reset link is no longer valid."}{" "}
              Reset links expire after a short window, so request a fresh one and use it right
              away. You can also skip passwords entirely and use "Continue with Google" on the sign
              in page.
            </p>
            <Button
              type="button"
              className="mt-7 w-full uppercase"
              onClick={() => navigate({ to: "/forgot-password" })}
            >
              Request a new link
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <p className="text-sm text-muted-foreground">
              {accountEmail
                ? `You are setting the password for ${accountEmail}.`
                : "You are setting the password for the account this link was sent to."}
            </p>
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">At least 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                name="confirm"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full uppercase">
              {busy ? "Saving..." : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
